import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  PlusIcon,
  SearchIcon,
  UserIcon,
  ClockIcon,
  CalendarIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UsersIcon,
  HomeIcon, // ← Changed from BuildingOfficeIcon
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import { workersAPI } from "../api/workers";
import { laborAPI } from "../api/labor";
import { projectsAPI } from "../api/projects";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/common/LoadingSpinner";
import WorkerForm from "../components/labor/WorkerForm";
import ClockInOut from "../components/labor/ClockInOut";
import toast from "react-hot-toast";

const LaborPage = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("workers");
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [selectedProject, setSelectedProject] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const queryClient = useQueryClient();

  // Fetch workers
  const { data: workersData, isLoading: workersLoading } = useQuery({
    queryKey: ["workers", searchTerm],
    queryFn: () => workersAPI.getAll({ search: searchTerm || undefined }),
  });

  // Fetch projects for dropdown
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects", user?.role],
    queryFn: () => projectsAPI.getAll(),
  });

  // Fetch timesheets
  const { data: timesheetsData, isLoading: timesheetsLoading } = useQuery({
    queryKey: ["timesheets"],
    queryFn: () => laborAPI.getTimesheets(),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => workersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["workers"]);
      toast.success("Worker deleted successfully");
    },
    onError: () => toast.error("Failed to delete worker"),
  });

  const workers = workersData?.data || [];
  const projects = projectsData?.data?.projects || [];
  const timesheets = timesheetsData?.data || [];

  // Filter workers based on role
  const getFilteredWorkers = () => {
    const role = user?.role;

    if (role === "super_admin" || role === "project_manager") {
      return workers;
    }

    if (role === "sub_manager") {
      // Get user's assigned project IDs
      const userProjects = projects.filter(
        (p) => p.assigned_users?.includes(user.id) || p.manager_id === user.id,
      );
      const projectIds = userProjects.map((p) => p.id);
      return workers.filter((w) => projectIds.includes(w.project_id));
    }

    if (role === "foreman") {
      return workers.filter((w) => w.project_id === user.project_id);
    }

    if (role === "worker") {
      return workers.filter((w) => w.id === user.id);
    }

    return workers;
  };

  // Filter projects based on role
  const getFilteredProjects = () => {
    const role = user?.role;

    if (role === "super_admin" || role === "project_manager") {
      return projects;
    }

    if (role === "sub_manager" || role === "foreman") {
      return projects.filter(
        (p) => p.assigned_users?.includes(user.id) || p.manager_id === user.id,
      );
    }

    return [];
  };

  // Group workers by project
  const groupWorkersByProject = (workerList) => {
    const grouped = {};

    workerList.forEach((worker) => {
      const projectId = worker.project_id || "unassigned";
      if (!grouped[projectId]) {
        grouped[projectId] = {
          projectId: projectId,
          projectName: worker.project_name || "Unassigned",
          workers: [],
        };
      }
      grouped[projectId].workers.push(worker);
    });

    return Object.values(grouped);
  };

  const filteredWorkers = getFilteredWorkers();
  const filteredProjects = getFilteredProjects();
  const groupedWorkers = groupWorkersByProject(filteredWorkers);

  // Toggle project expansion
  const toggleProject = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const isProjectExpanded = (projectId) => {
    return expandedProjects[projectId] !== undefined
      ? expandedProjects[projectId]
      : true;
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this worker?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleProjectChange = (workerId, projectId) => {
    setSelectedProject((prev) => ({ ...prev, [workerId]: projectId }));
  };

  const canManage =
    user?.role === "super_admin" ||
    user?.role === "project_manager" ||
    user?.role === "sub_manager";
  const canDelete = user?.role === "super_admin";

  if (workersLoading || timesheetsLoading || projectsLoading)
    return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            👷 Labor Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {user?.role === "worker"
              ? "View your timesheet and clock in/out"
              : user?.role === "foreman"
                ? "Manage your team and site operations"
                : "Manage workers grouped by project"}
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowWorkerModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Add Worker
          </button>
        )}
      </div>

      {/* Search - only for Admin/PM/Sub Manager/Foreman */}
      {user?.role !== "worker" && (
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search workers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab("workers")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "workers"
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <UsersIcon className="h-4 w-4 inline mr-2" />
            Workers ({filteredWorkers.length})
          </button>
          <button
            onClick={() => setActiveTab("timesheets")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "timesheets"
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <ClockIcon className="h-4 w-4 inline mr-2" />
            Timesheets ({timesheets.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="mt-6">
        {activeTab === "workers" && (
          <>
            {filteredWorkers.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  No workers found
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {user?.role === "worker"
                    ? "You are the only worker in the system"
                    : "Add your first worker to get started"}
                </p>
                {canManage && (
                  <button
                    onClick={() => setShowWorkerModal(true)}
                    className="btn-primary mt-4 inline-flex items-center gap-2"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add Worker
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {groupedWorkers.map((group) => {
                  const isExpanded = isProjectExpanded(group.projectId);

                  return (
                    <div key={group.projectId} className="space-y-3">
                      {/* Project Header */}
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 p-2 rounded-lg transition-colors"
                        onClick={() => toggleProject(group.projectId)}
                      >
                        <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                        <HomeIcon className="h-5 w-5 text-primary-500" />
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {group.projectName}
                        </span>
                        <span className="text-sm text-slate-400">
                          ({group.workers.length} workers)
                        </span>
                      </div>

                      {/* Workers Grid */}
                      {isExpanded && (
                        <div className="pl-4 border-l-2 border-primary-200 dark:border-primary-800">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {group.workers.map((worker, index) => (
                              <motion.div
                                key={worker.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="card hover:shadow-lg transition-all duration-300"
                              >
                                {/* Worker Header */}
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {worker.full_name
                                      ?.split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .toUpperCase() || "U"}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                                      {worker.full_name}
                                    </h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                      {worker.specialty || "General Worker"}
                                    </p>
                                  </div>
                                  <span
                                    className={`badge ${worker.is_active ? "badge-success" : "badge-danger"}`}
                                  >
                                    {worker.is_active ? "Active" : "Inactive"}
                                  </span>
                                </div>

                                {/* Worker Details */}
                                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <p className="text-slate-500 dark:text-slate-400">
                                      Hourly Rate
                                    </p>
                                    <p className="font-medium text-slate-900 dark:text-white">
                                      ${worker.hourly_rate || "0.00"}/hr
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 dark:text-slate-400">
                                      Skill Level
                                    </p>
                                    <p className="font-medium text-slate-900 dark:text-white">
                                      {worker.skill_level || "N/A"}
                                    </p>
                                  </div>
                                </div>

                                {/* Clock In/Out Section */}
                                {(user?.role !== "worker" ||
                                  worker.id === user?.id) && (
                                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <div className="flex flex-col gap-2">
                                      {/* Project Selector */}
                                      {filteredProjects.length > 0 &&
                                        user?.role !== "worker" && (
                                          <div className="flex items-center gap-2">
                                            <label className="text-xs text-slate-500 dark:text-slate-400">
                                              Project:
                                            </label>
                                            <select
                                              value={
                                                selectedProject[worker.id] || ""
                                              }
                                              onChange={(e) =>
                                                handleProjectChange(
                                                  worker.id,
                                                  parseInt(e.target.value),
                                                )
                                              }
                                              className="flex-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500"
                                            >
                                              <option value="">
                                                Select Project
                                              </option>
                                              {filteredProjects.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                  {p.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        )}

                                      {/* Clock In/Out Button */}
                                      <ClockInOut
                                        workerId={worker.id}
                                        projectId={
                                          selectedProject[worker.id] || null
                                        }
                                        onSuccess={() => {
                                          queryClient.invalidateQueries([
                                            "timesheets",
                                          ]);
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Action Buttons */}
                                {canManage && (
                                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => setEditingWorker(worker)}
                                      className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                      title="Edit Worker"
                                    >
                                      <PencilIcon className="h-4 w-4 text-slate-400" />
                                    </button>
                                    {canDelete && (
                                      <button
                                        onClick={() => handleDelete(worker.id)}
                                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        title="Delete Worker"
                                      >
                                        <TrashIcon className="h-4 w-4 text-red-400 hover:text-red-500" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === "timesheets" && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Worker
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Hours
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Project
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {timesheets.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center py-8 text-slate-500 dark:text-slate-400"
                    >
                      No timesheets found
                    </td>
                  </tr>
                ) : (
                  timesheets.slice(0, 10).map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-slate-100 dark:border-slate-700/50"
                    >
                      <td className="py-3 px-4 text-sm text-slate-900 dark:text-white">
                        {entry.worker_name || "Unknown"}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                        {entry.date
                          ? new Date(entry.date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900 dark:text-white">
                        {entry.total_hours || 0}h
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                        {entry.project_name || "N/A"}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`badge ${entry.is_approved ? "badge-success" : "badge-warning"}`}
                        >
                          {entry.is_approved ? "✅ Approved" : "⏳ Pending"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {canManage && (
        <>
          {showWorkerModal && (
            <WorkerForm
              onClose={() => setShowWorkerModal(false)}
              onSuccess={() => {
                queryClient.invalidateQueries(["workers"]);
                setShowWorkerModal(false);
              }}
            />
          )}

          {editingWorker && (
            <WorkerForm
              worker={editingWorker}
              onClose={() => setEditingWorker(null)}
              onSuccess={() => {
                queryClient.invalidateQueries(["workers"]);
                setEditingWorker(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default LaborPage;
